// 768px is used as our `minWidth` value for content
// 960px is used as our `minWidth` for our OffCanvas component
// 1152px
// 1400px is used as our `maxWidth` value for content
export const breakpoints = ['768px', '960px', '1152px', '1400px'] as const

// NOTE: this value is the same as muiTheme.palette.grey[300] for easier MUI migration
const BORDER_GRAY = '#e0e0e0'

// Box shadows
// Separating these out by "type" allows us to mix/match colors and strengths as needed
//

const boxShadowColors = {
  light: 'rgba(0, 0, 0, 0.08)',
  medium: 'rgba(0, 0, 0, 0.12)',
  dark: 'rgba(0, 0, 0, 0.16)'
}

const boxShadowOffsets = {
  small: ['0 0.3px 0.5px', '0 2px 4px'],
  medium: '0 4px 8px',
  large: '0 8px 16px'
}

const boxShadows = {
  small: `${boxShadowOffsets.small[0]} ${boxShadowColors.light}, ${boxShadowOffsets.small[1]} ${boxShadowColors.medium}`,
  medium: `${boxShadowOffsets.medium} ${boxShadowColors.light}`,
  large: `${boxShadowOffsets.large} ${boxShadowColors.dark}`
}

const theme = {
  fonts: {
    base: 'Sohne VF, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    mono: "'Sohne Mono', Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace"
  },

  /**
   * theme.colors: contains standard app colors
   * color organization is inspired by https://yeun.github.io/open-color/
   */
  colors: {
    red: ['#e6325a', '#991e39', '#fceaee'],
    green: ['#23c386', '#1b9868', '#c3fddf', '#0e6655', '#e9f9f6'],
    blue: ['#1557b2', '#104184', '#e9f4fe'],
    teal: ['#429a98', '#337675'],
    yellow: ['#fbb530', '#f3a105'],
    orange: ['#f57300'],
    purple: ['#9769FF', '#7436ff', '#CE93D8'],
    white: ['#fff'],
    whiteOverlay: ['rgba(255, 255, 255, 0.08)'],
    black: ['#000'],
    gray: ['#f2f2f2', BORDER_GRAY, '#ccc'],
    steel: ['#b3b3b3', '#797979', '#333'],
    panel: ['#fbfbfb'],
    border: BORDER_GRAY,
    borderContrast: ['rgba(151, 151, 151, 0.2)'],
    grayOverlay: ['rgba(242,242,242,0.5)']
  } as const,

  codeblock: {
    dark: '#2b2b2b' // This is coming from a 3rd party rendering lib, so adding this as a variable so we can re-use when needed to emulate
  },

  opacity: [0.5, 0.25],
  // theme.fontSizes[]
  // 0 = 12
  // 1 = 14
  // 2 = 16
  // 3 = 20
  // 4 = 24
  // 5 = 32
  // 6 = 48
  // 7 = 64
  // 8 = 72
  fontSizes: [12, 14, 16, 20, 24, 32, 48, 64, 72],
  fontWeights: [400, 600],
  // theme.space[]
  // 0 = 0
  // 1 = 4
  // 2 = 8
  // 3 = 16
  // 4 = 24
  // 5 = 32
  // 6 = 40
  // 7 = 64
  // 8 = 128
  // 9 = 256
  // 10 = 512
  space: [0, 4, 8, 16, 24, 32, 40, 64, 128, 256, 512] as const,
  letterSpacing: [0, 0.25, 0.5, 0.75, 1, 2],
  transition: ['all 300ms ease'],
  /*
   theme.bp: contains raw widths of breakpoints
   */
  bp: breakpoints,
  /*
  theme.mq: is used for media queries
  {
    color: blue;
    [theme.mq[0]]: {
      color: theme.colors.green[0],
      '&:hover': {
        color: theme.colors.green[1]
      }
    },
    [theme.mq[0]]: {
      color: theme.colors.red[0]
    }
  }
   */
  mq: breakpoints.map((bp) => `@media(min-width: ${bp})`),

  border: {
    base: `1px solid ${BORDER_GRAY}`
  },

  /*
  theme.borderRadius: standardized border radii
  @NOTE [2] provides flexibility to have perfectly rounded edges; regardless of height, that work well for things like avatars and our buttons
  {
    borderRadius: theme.borderRadius[0] // Standard border radius
  }
  */
  borderRadius: [4, 8, 9999],

  boxShadowColors,
  boxShadowOffsets,
  boxShadows,

  // @NOTE Only our toast container has [7] since it should appear above everything else
  // @NOTE react-modal uses 3000 so toast uses 5000 to be sure its above that
  // toast alerts should not be grayed out for modals because we have forms in modals
  // and the feedback from those forms should be clearly visible
  zIndices: [0, 100, 200, 300, 600, 900, 3000, 5000],
  spring: {
    stiff: { mass: 1, tension: 256, friction: 32 }
  }
}

export default theme
